import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Phone, Mail, MapPin, Heart, Send, Clock } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [searchParams] = useSearchParams();
  const kittenId = searchParams.get('kitten');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    kitten_id: kittenId || '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (kittenId) {
      setFormData((prev) => ({ ...prev, kitten_id: kittenId }));
    }
  }, [kittenId]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        kitten_id: '',
      });
    } catch {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FAF8F5]">
      <SEO 
        title="Contact KittenState | Scottish Fold & Scottish Straight Kittens"
        description="Get in touch with KittenState to inquire about our Scottish Fold and Scottish Straight kittens. Visit us in Covington Township, PA or call (929) 333-1936."
        keywords="contact KittenState, Scottish Fold breeder Pennsylvania, kitten inquiry, Covington Township PA cattery"
        url="https://kittenstate.com/contact"
      />

      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-rose-50 to-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E85D75] mb-3">
            We'd Love to Hear From You
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-stone-900 mb-4">
            Get in Touch
          </h1>
          <p className="text-base lg:text-lg text-stone-600 max-w-2xl mx-auto">
            Have questions about our kittens or adoption process? We're here to help!
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Contact Form */}
            <div className="bg-white p-8 rounded-3xl border border-rose-100/50 shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <Send className="w-5 h-5 text-[#E85D75]" />
                <h2 className="text-2xl font-serif font-semibold text-stone-800">Send a Message</h2>
              </div>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2 block">
                    Your Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="contact-name-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12 bg-white border-rose-100 focus:border-[#E85D75] focus:ring-[#E85D75]/20 rounded-xl"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2 block">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="contact-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12 bg-white border-rose-100 focus:border-[#E85D75] focus:ring-[#E85D75]/20 rounded-xl"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2 block">
                    Phone Number
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    data-testid="contact-phone-input"
                    value={formData.phone}
                    onChange={handleChange}
                    className="h-12 bg-white border-rose-100 focus:border-[#E85D75] focus:ring-[#E85D75]/20 rounded-xl"
                    placeholder="(123) 456-7890"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2 block">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="bg-white border-rose-100 focus:border-[#E85D75] focus:ring-[#E85D75]/20 rounded-xl"
                    placeholder="Tell us about your interest in our kittens..."
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={loading}
                  className="w-full bg-[#E85D75] text-white hover:bg-[#D64560] shadow-lg shadow-[#E85D75]/25 rounded-full px-8 py-6 text-base font-medium transition-all hover:-translate-y-0.5 disabled:opacity-50"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <div className="bg-white p-8 rounded-3xl border border-rose-100/50 shadow-sm">
                <h2 className="text-2xl font-serif font-semibold text-stone-800 mb-6">
                  Contact Information
                </h2>
                <div className="space-y-5">
                  <a href="tel:929-333-1936" className="flex items-center gap-4 group">
                    <div className="w-12 h-12 rounded-2xl bg-rose-50 flex items-center justify-center group-hover:bg-[#E85D75] transition-colors">
                      <Phone className="w-5 h-5 text-[#E85D75] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="text-xs text-stone-500 uppercase tracking-wider">Phone</p>
                      <p className="text-lg font-medium text-stone-800 group-hover:text-[#E85D75] transition-colors">929-333-1936</p>
                    </div>
                  </a>

                  <a href="mailto:sales@kittenstate.com" className="flex items-center gap-4 group">
                    <div className="w-12 h-12 rounded-2xl bg-rose-50 flex items-center justify-center group-hover:bg-[#E85D75] transition-colors">
                      <Mail className="w-5 h-5 text-[#E85D75] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="text-xs text-stone-500 uppercase tracking-wider">Email</p>
                      <p className="text-lg font-medium text-stone-800 group-hover:text-[#E85D75] transition-colors">sales@kittenstate.com</p>
                    </div>
                  </a>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-rose-50 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-[#E85D75]" />
                    </div>
                    <div>
                      <p className="text-xs text-stone-500 uppercase tracking-wider">Hours</p>
                      <p className="text-lg font-medium text-stone-800">Mon - Sat: 9am - 7pm</p>
                      <p className="text-sm text-stone-500">Sun: By appointment</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-rose-50 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#E85D75]" />
                    </div>
                    <div>
                      <p className="text-xs text-stone-500 uppercase tracking-wider">Location</p>
                      <p className="text-lg font-medium text-stone-800">Covington Township, PA 18424</p>
                      <p className="text-sm text-stone-500">Visits by appointment only</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Promise Card */}
              <div className="bg-gradient-to-br from-rose-50 to-pink-50 p-8 rounded-3xl border border-rose-100">
                <Heart className="w-8 h-8 text-[#E85D75] mb-4" />
                <h3 className="text-xl font-serif font-semibold text-stone-800 mb-3">
                  Our Promise
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  We respond to all inquiries within 24 hours. Whether you have questions about a specific 
                  kitten or want to learn more about our adoption process, we're here to help you find 
                  your perfect furry companion.
                </p>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-4">
                <a
                  href="tel:929-333-1936"
                  className="bg-white p-4 rounded-2xl border border-rose-100/50 text-center hover:shadow-md hover:border-rose-200 transition-all"
                >
                  <Phone className="w-6 h-6 text-[#E85D75] mx-auto mb-2" />
                  <p className="text-sm font-medium text-stone-800">Call Now</p>
                </a>
                <a
                  href="mailto:sales@kittenstate.com"
                  className="bg-white p-4 rounded-2xl border border-rose-100/50 text-center hover:shadow-md hover:border-rose-200 transition-all"
                >
                  <Mail className="w-6 h-6 text-[#E85D75] mx-auto mb-2" />
                  <p className="text-sm font-medium text-stone-800">Email Us</p>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
